import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Field, reduxForm, change } from 'redux-form';
import { Button, ButtonToolbar, Modal, ModalHeader, ModalBody } from 'reactstrap';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import moment from 'moment';
import renderCheckBoxField from '../../../../shared/components/form/CheckBox';
import { getCorprateUrl } from '../../../../shared/components/domainSupport';
import Tooltip from '../../../../shared/components/widgets/Tooltip';
import { saveBudgetAccount } from '../../shared/budgetApi';

const BudgetAccountSettingsModal = ({
  isOpen,
  retailer,
  retailerConfig,
  budgetAccountData,
  setForceRefresh,
  onCancel,
  handleSubmit,
}) => {
  const selectedSeller = useSelector(store => store.sellerSelect);
  const user = useSelector(store => store.authentication.user);
  const theme = useSelector(store => store.theme);
  const [showError, setShowError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const dispatch = useDispatch();

  const portfolioName = `${JSON.parse(selectedSeller.agency).name} - BG Account Portfolio`;

  useEffect(() => {
    dispatch(change('BudgetAccountSettingsModalForm', 'budgetAccountDailyId', budgetAccountData?.budget_account_daily_id));
    // Always set to ROAS
    dispatch(change('BudgetAccountSettingsModalForm', 'costMetricView', 'roas'));
    dispatch(change('BudgetAccountSettingsModalForm', 'portfolioName', portfolioName));
    // Set checkbox based on existing data - true if already approved (1), false if not
    const isApproved = budgetAccountData?.terms_approval === 1;
    dispatch(change('BudgetAccountSettingsModalForm', 'modalTermsApproval', isApproved));
  }, [budgetAccountData, retailer]);

  const submitForm = async (formData) => {
    setIsSubmitting(true);
    setShowError('');

    const submitBudgetAccountDaily = {
      accountId: selectedSeller.value,
      budgetAccountDailyId: formData.budgetAccountDailyId || 0,
      costMetricView: 'roas', // Always ROAS
      portfolioName: formData.portfolioName,
      termsApproval: formData.modalTermsApproval, // Use the modal field name
      retailer, // Include retailer in the request
    };

    try {
      await saveBudgetAccount({ data: submitBudgetAccountDaily, jwtToken: user.jwtToken });
      setForceRefresh(moment().utc());
      setIsSubmitting(false);
    } catch (error) {
      setShowError('We were unable to update your Budget Account Settings');
      setIsSubmitting(false);
    }
  };

  const modalClasses = classNames({
    'modal-dialog--primary': true,
  }, theme.className, 'ltr-support');

  // Note: For Walmart, portfolio is not used/needed
  const showPortfolio = retailerConfig?.key !== 'walmart';

  return (
    <Modal
      isOpen={isOpen}
      backdrop="static"
      keyboard={false}
      className={modalClasses}
    >
      <ModalHeader>
        <h3>Budget Account Settings Required</h3>
        <p className="typography-message">
          Please configure your budget account settings for <strong>{retailerConfig?.name || retailer}</strong> before continuing.
        </p>
      </ModalHeader>
      <ModalBody style={{ textAlign: 'left' }}>
        <form className="form" onSubmit={handleSubmit(submitForm)}>
          <div className="form__form-group">
            <span className="form__form-group-label">
              <h4>Cost Metric View:</h4>
            </span>
            <div>
              <p><strong>ROAS</strong> (Return on Ad Spend)</p>
              <p className="typography-message" style={{ fontSize: '0.9em', marginTop: '5px' }}>
                All cost targets will be set using ROAS values. PACoS equivalents will be shown for reference.
              </p>
            </div>
          </div>

          {showPortfolio && (
            <div className="form__form-group">
              <span className="form__form-group-label typography-message">
                <h4>
                  Default Portfolio:{' '}
                  <Tooltip
                    id="portfolioName"
                    msg="Portfolio that will be used to manage launched campaigns"
                    placement="top"
                  />
                </h4>
              </span>
              <div>
                <p>Portfolio Name: {portfolioName}</p>
                <p>Portfolio ID: {budgetAccountData?.portfolio_id || 'Create during first launch'}</p>
                <br />
              </div>
            </div>
          )}

          <div className="form__form-group">
            <h4>REQUIREMENTS, ACKNOWLEDGEMENTS, TERMS, AND CONDITIONS</h4>
            <br />
            <a href={`${getCorprateUrl()}/terms-of-use`} target="_blank" rel="noopener noreferrer">
              Terms &amp; Conditions
            </a>
            <br />
            <p>
              Ensure All Ads Created on this Platform for this Account(s) will NOT EDIT ANY Campaigns
              located in the Account Portfolio at any time.
            </p>
            <br />
            <div className="form__form-group-field">
              <Field
                name="modalTermsApproval"
                component={renderCheckBoxField}
                label="I Approve the Terms and Conditions"
              />
            </div>
          </div>

          {showError && (
            <div className="form__form-group-error">&nbsp;&nbsp;&nbsp;Error: {showError}</div>
          )}

          <div className="form__form-group">
            <br />
          </div>

          <div className="form__form-group">
            <ButtonToolbar className="form__button-toolbar">
              <Button color="primary" type="submit" disabled={isSubmitting}>
                {isSubmitting ? 'Saving...' : 'Save Settings'}
              </Button>
              <Button color="secondary" type="button" onClick={onCancel} disabled={isSubmitting}>
                Cancel - Go to Account Tab
              </Button>
            </ButtonToolbar>
          </div>
        </form>
      </ModalBody>
    </Modal>
  );
};

const validate = (values) => {
  const errors = {};

  if (!values.modalTermsApproval) {
    errors.modalTermsApproval = 'You must accept the Terms and Conditions';
  }

  return errors;
};

BudgetAccountSettingsModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  retailer: PropTypes.string.isRequired,
  retailerConfig: PropTypes.shape({
    name: PropTypes.string,
    key: PropTypes.string,
  }),
  budgetAccountData: PropTypes.shape({
    budget_account_daily_id: PropTypes.number,
    cost_metric_view: PropTypes.string,
    portfolio_id: PropTypes.string,
    terms_approval: PropTypes.number,
  }),
  setForceRefresh: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
};

BudgetAccountSettingsModal.defaultProps = {
  budgetAccountData: {},
  retailerConfig: {},
};

export default reduxForm({
  form: 'BudgetAccountSettingsModalForm', // Unique form name to avoid collision with BudgetAccountEditForm
  validate,
  enableReinitialize: true,
})(BudgetAccountSettingsModal);
